// ============================================================
// types/configuracion.types.ts — Config keys del backend
// Editables vía PUT /configuraciones/{clave}
// ============================================================

export interface ConfiguracionItem {
  clave: string;
  valor: string;
  tipo: 'string' | 'boolean' | 'decimal' | 'entero' | 'imagen' | 'time' | 'select';
  descripcion: string;
}

// Claves conocidas (los datos institucionales IE_* se gestionan via /api/v1/institucion)
export type ConfigClave =
  | 'MAX_ESTUDIANTES' | 'MAX_DOCENTES'
  | 'NOMINA_REQUIERE_APROBACION' | 'NOMINA_PERMITE_RECTIFICACION'
  | 'EVAL_NOTA_MINIMA_APROBATORIA' | 'EVAL_MAX_AREAS_RECUPERACION'
  | 'EVAL_MIN_AREAS_REPITENCIA' | 'EVAL_USA_COMPETENCIAS' | 'EVAL_ESCALA_LITERAL'
  | 'TRASLADO_REQUIERE_APROBACION' | 'TRASLADO_VALIDAR_DEUDA'
  | 'PDF_MEMBRETE_ACTIVO' | 'PDF_CODIGO_VERIFICADOR'
  | 'ASISTENCIA_UMBRAL_INASISTENCIAS'
  | 'ASISTENCIA_DIARIA_HORA_ENTRADA'
  | 'ASISTENCIA_DIARIA_HORA_TARDANZA_LIMITE'
  | 'BOLETA_FUENTE_ASISTENCIA'
  | 'FUM_OBLIGATORIA_AL_MATRICULAR'
  | 'COBRO_EMITE_COMPROBANTE' | 'COBRO_TIPO_COMPROBANTE'
  | 'COBRO_SERIE_BOLETA' | 'COBRO_SERIE_FACTURA'
  | 'COBRO_RUC' | 'COBRO_PERMITE_EDICION_DIAS'
  | 'COBRO_ADICIONAL_EMITE_COMPROBANTE'
  | 'RENIEC_HABILITADO' | 'RENIEC_API_URL' | 'RENIEC_API_TOKEN'
  | 'MAIL_HABILITADO' | 'MAIL_DESTINATARIO_ADMIN'
  | 'IE_SELLO_BASE64';

export interface ConfiguracionPayload {
  valor: string;
}

// Registro de historial de cambios — GET /configuraciones/historial
export interface ConfiguracionHistorial {
  idHistorico:       number;
  clave:             string;
  valorAnterior:     string | null;
  valorNuevo:        string;
  modificadoPor:     string;
  ip:                string | null;
  fechaModificacion: string; // ISO datetime
}

// Grupos para organizar el formulario
export interface ConfigGroup {
  titulo: string;
  descripcion: string;
  claves: ConfigClave[];
}

export const CONFIG_GROUPS: ConfigGroup[] = [
  {
    titulo: 'Límites del Sistema',
    descripcion: 'Capacidad máxima de estudiantes y docentes activos',
    claves: ['MAX_ESTUDIANTES', 'MAX_DOCENTES'],
  },
  {
    titulo: 'Nómina de Matrícula',
    descripcion: 'Flujo de aprobación y envío a UGEL',
    claves: ['NOMINA_REQUIERE_APROBACION', 'NOMINA_PERMITE_RECTIFICACION'],
  },
  {
    titulo: 'Evaluación y Promoción',
    descripcion: 'Umbrales y escalas de calificación',
    claves: ['EVAL_NOTA_MINIMA_APROBATORIA', 'EVAL_MAX_AREAS_RECUPERACION', 'EVAL_MIN_AREAS_REPITENCIA', 'EVAL_USA_COMPETENCIAS', 'EVAL_ESCALA_LITERAL'],
  },
  {
    titulo: 'Traslados',
    descripcion: 'Validaciones del proceso de traslado',
    claves: ['TRASLADO_REQUIERE_APROBACION', 'TRASLADO_VALIDAR_DEUDA'],
  },
  {
    titulo: 'Documentos PDF',
    descripcion: 'Configuración de documentos oficiales',
    claves: ['PDF_MEMBRETE_ACTIVO', 'PDF_CODIGO_VERIFICADOR', 'IE_SELLO_BASE64'],
  },
  {
    titulo: 'Asistencia',
    descripcion: 'Alertas y umbrales de inasistencia',
    claves: ['ASISTENCIA_UMBRAL_INASISTENCIAS'],
  },
  {
    titulo: 'Asistencia Diaria (Portería)',
    descripcion: 'Horarios de entrada, tolerancia para tardanzas y fuente del resumen en boletas',
    claves: [
      'ASISTENCIA_DIARIA_HORA_ENTRADA',
      'ASISTENCIA_DIARIA_HORA_TARDANZA_LIMITE',
      'BOLETA_FUENTE_ASISTENCIA',
    ],
  },
  {
    titulo: 'FUM y Matrícula',
    descripcion: 'Requisitos del proceso de matrícula',
    claves: ['FUM_OBLIGATORIA_AL_MATRICULAR'],
  },
  {
    titulo: 'Integración RENIEC',
    descripcion: 'Consulta automática de datos por DNI al registrar usuarios y tutores',
    claves: ['RENIEC_HABILITADO', 'RENIEC_API_URL', 'RENIEC_API_TOKEN'],
  },
  {
    titulo: 'Correo Electrónico',
    descripcion: 'Envío de notificaciones automáticas por email',
    claves: ['MAIL_HABILITADO', 'MAIL_DESTINATARIO_ADMIN'],
  },
  {
    titulo: 'Facturación SUNAT',
    descripcion: 'Configuración para emisión de comprobantes de pago',
    claves: [
      'COBRO_EMITE_COMPROBANTE', 'COBRO_TIPO_COMPROBANTE',
      'COBRO_SERIE_BOLETA', 'COBRO_SERIE_FACTURA',
      'COBRO_RUC', 'COBRO_PERMITE_EDICION_DIAS',
      'COBRO_ADICIONAL_EMITE_COMPROBANTE',
    ],
  },
];

export interface ConfigOption {
  value:        string;
  label:        string;
  description?: string;   // Tooltip explicativo de la opción
}

/** Aviso adicional que se muestra al confirmar el cambio a un valor específico. */
export interface ConfigConfirmAviso {
  cuandoValor: string;    // Si el valor nuevo es exactamente este…
  mensaje:     string;    // …muestra este aviso en el modal de confirmación.
}

export const CONFIG_META: Record<ConfigClave, {
  label:       string;
  tipo:        'string' | 'boolean' | 'decimal' | 'entero' | 'imagen' | 'time' | 'select';
  hint?:       string;
  hintLevel?:  'info' | 'warning';
  min?:        number;
  options?:    ConfigOption[];
  avisoConfirmar?: ConfigConfirmAviso[];
}> = {
  MAX_ESTUDIANTES:               { label: 'Límite de estudiantes activos', tipo: 'entero', hint: 'Capacidad máxima del colegio. Ej: 500. Debe ser un número positivo.', min: 1 },
  MAX_DOCENTES:                  { label: 'Límite de docentes activos',    tipo: 'entero', hint: 'Ingresa 0 para no aplicar límite. Si > 0, no se podrán crear más docentes al alcanzar el tope.', hintLevel: 'warning', min: 0 },
  NOMINA_REQUIERE_APROBACION:    { label: 'Nómina requiere aprobación del director', tipo: 'boolean' },
  NOMINA_PERMITE_RECTIFICACION:  { label: 'Permite rectificar nóminas enviadas', tipo: 'boolean' },
  EVAL_NOTA_MINIMA_APROBATORIA:  { label: 'Nota mínima aprobatoria', tipo: 'decimal', hint: 'Escala 0-20, ej: 11' },
  EVAL_MAX_AREAS_RECUPERACION:   { label: 'Máx. áreas desaprobadas para recuperación', tipo: 'entero' },
  EVAL_MIN_AREAS_REPITENCIA:     { label: 'Mín. áreas desaprobadas para repitencia', tipo: 'entero' },
  EVAL_USA_COMPETENCIAS:         { label: 'Habilitar módulo de competencias', tipo: 'boolean' },
  EVAL_ESCALA_LITERAL:           { label: 'Usar escala literal AD/A/B/C en boletas', tipo: 'boolean' },
  TRASLADO_REQUIERE_APROBACION:  { label: 'Traslado requiere aprobación del director', tipo: 'boolean' },
  TRASLADO_VALIDAR_DEUDA:        { label: 'Bloquear traslado si hay deuda pendiente', tipo: 'boolean' },
  PDF_MEMBRETE_ACTIVO:           { label: 'Incluir membrete en documentos PDF', tipo: 'boolean' },
  PDF_CODIGO_VERIFICADOR:        { label: 'Incluir código verificador en constancias', tipo: 'boolean' },
  ASISTENCIA_UMBRAL_INASISTENCIAS: { label: '% de inasistencias para alerta', tipo: 'entero', hint: 'Ej: 30 = alerta al superar el 30%' },
  ASISTENCIA_DIARIA_HORA_ENTRADA:         { label: 'Hora de entrada al colegio', tipo: 'time', hint: 'Hora oficial de inicio de clases. Ej: 07:30' },
  ASISTENCIA_DIARIA_HORA_TARDANZA_LIMITE: { label: 'Límite de tardanza',          tipo: 'time', hint: 'Después de esta hora se considera ausencia. Ej: 08:30' },
  BOLETA_FUENTE_ASISTENCIA: {
    label: 'Fuente del resumen de asistencia en la boleta',
    tipo:  'select',
    hint:  'Determina de dónde se calculan los conteos de asistencia que aparecen en la boleta de notas.',
    options: [
      {
        value: 'POR_CURSO',
        label: 'Por curso',
        description: 'El resumen de asistencia en la boleta cuenta los registros que el docente toma en cada curso.',
      },
      {
        value: 'DIARIA',
        label: 'Diaria (portería)',
        description: 'El resumen de asistencia en la boleta cuenta el registro único al ingreso al colegio (portería).',
      },
    ],
    avisoConfirmar: [
      {
        cuandoValor: 'DIARIA',
        mensaje:
          'Atención: la asistencia diaria al colegio empezó a registrarse el día que activaste el módulo de portería. ' +
          'Las boletas de períodos anteriores a esa fecha mostrarán conteos en cero. ' +
          'Confirma que ya hay datos registrados antes de cambiar la fuente.',
      },
    ],
  },
  FUM_OBLIGATORIA_AL_MATRICULAR:   { label: 'FUM obligatoria al matricular', tipo: 'boolean', hint: 'Si está activo, no se puede generar boleta/constancia sin FUM completa' },
  COBRO_EMITE_COMPROBANTE:         { label: 'Emitir comprobante de pago (SUNAT)', tipo: 'boolean' },
  COBRO_TIPO_COMPROBANTE:          { label: 'Tipo de comprobante por defecto', tipo: 'string', hint: 'BOLETA o FACTURA' },
  COBRO_SERIE_BOLETA:              { label: 'Serie de boleta', tipo: 'string', hint: 'Ej: B001' },
  COBRO_SERIE_FACTURA:             { label: 'Serie de factura', tipo: 'string', hint: 'Ej: F001' },
  COBRO_RUC:                       { label: 'RUC de la institución', tipo: 'string', hint: '11 dígitos' },
  COBRO_PERMITE_EDICION_DIAS:          { label: 'Días permitidos para editar un cobro', tipo: 'entero', hint: 'Ej: 3 = solo editable 3 días después del registro' },
  COBRO_ADICIONAL_EMITE_COMPROBANTE:   { label: 'Emitir comprobante SUNAT al pagar cobros adicionales', tipo: 'boolean', hint: 'Si está activo, los cobros adicionales emiten boleta/factura SUNAT al ser pagados' },
  RENIEC_HABILITADO:               { label: 'Activar consulta RENIEC', tipo: 'boolean' },
  RENIEC_API_URL:                  { label: 'URL del servicio RENIEC', tipo: 'string', hint: 'Ej: https://api.reniec.pe/v1' },
  RENIEC_API_TOKEN:                { label: 'Token de autenticación RENIEC', tipo: 'string', hint: 'Token API — se guarda encriptado' },
  MAIL_HABILITADO:                 { label: 'Activar envío de correos', tipo: 'boolean' },
  MAIL_DESTINATARIO_ADMIN:         { label: 'Email del administrador', tipo: 'string', hint: 'Recibe alertas críticas del sistema' },
  IE_SELLO_BASE64:                 { label: 'Sello / Timbre de la IE', tipo: 'imagen', hint: 'PNG o JPG. Se imprime en boletas, certificados y documentos oficiales.' },
};
